/**
 * ===========================================================================
 *  AI SYSTEM — publishes `world.ai`
 * ===========================================================================
 *
 * Owns every non-player body on the field: what it wants, where it walks and
 * what animation state it is in. `src/physics/bodies.js` is still a stub, so
 * this module also integrates its units (see `steering.js`).
 *
 * Cost model — the whole thing has to fit in ~3 ms of CPU for 600 units:
 *
 *   census      O(live)   one pass, rebuilds the attack-token / ring counts
 *   squads      O(squads) each squad re-plans about 3x a second
 *   think       O(live/20) every unit re-decides every 0.18–0.40 s, staggered
 *               by deterministic spawn key so the load is flat, not spiky
 *   act+move    O(live)   cheap arithmetic + one spatial-hash query for
 *               separation
 *
 * Nothing here allocates after init. Every query writes into a pre-made
 * Int32Array; every vector is a pair of locals.
 *
 * Public API
 * ----------
 *   world.ai.hurt(id, amount, srcId, dirX, dirZ, heavy)   unit-vs-unit damage
 *   world.ai.enlist(id, squad, role)                      bind a spawned unit
 *   world.ai.discharge(id)                                unbind before release
 *   world.ai.newSquad(team, x, z, opts) -> idx
 *   world.ai.setSquadOrder(idx, order, gx, gz)
 *   world.ai.callToBattle(x, z, team, radius, maxSquads)
 *   world.ai.registerOfficer(id, name) / world.ai.officers / world.ai.engaged
 *   world.ai.ringInfo(targetId, radius)                   measurement probe
 *   world.ai.setDifficulty(name) / world.ai.tokens
 *   world.ai.stats  { cpuMs, units, aware, attacking, tokens, fleeing, ... }
 */
import { ORDER } from '../core/systems.js';
import { ST, KIND, TEAM, FLAG } from '../core/entities.js';
import { clamp, clamp01 } from '../core/mathx.js';
import { AIStore, ACT, ACT_NAME, ROLE, AIF, ORDERS, FORM, LOCKED, frac01, hash32 } from './store.js';
import { Squads } from './squad.js';
import { separate, integrate, deathSlide } from './steering.js';
import { think, act, react, dropToken, witness, resetGruntBattle } from './grunt.js';
import { createCommander, OBJ, OBJ_NAME } from './command.js';
import { officerAct, officerHit, makeOfficer, OF, OF_NAME } from './officer.js';

/**
 * `Math.hypot` is NOT a cheap intrinsic in V8: it is a variadic builtin that
 * boxes its arguments, and it measured at ~155 bytes of garbage per call
 * against ~0 for `Math.sqrt`. This module calls it once or more per unit per
 * fixed step, which is the whole of the AI's allocation rate and therefore the
 * whole of `aiCpuPeak` — the mean was never the problem, the scavenges landing
 * inside the unit loop were. Same arithmetic, no allocation.
 */
const hyp = (x, z) => Math.sqrt(x * x + z * z);


const DIFF = {
  // `grantGap` is the minimum spacing between two grants on the SAME target.
  // It is what stops two swings starting on the same frame, and it is also the
  // main lever on how much of the pool is in use: measured in ai_ring, 0.30 s
  // of dead time between grants held the ring at ~1.6 swings at once, 0.16 s
  // holds it at 2–3, which is the Dynasty Warriors read.
  easy:   { tokens: 2, tokensAI: 1, damageMul: 0.65, coolMin: 1.6, coolSpan: 2.4, grantGap: 0.34 },
  normal: { tokens: 3, tokensAI: 2, damageMul: 1.00, coolMin: 1.00, coolSpan: 1.8, grantGap: 0.16 },
  hard:   { tokens: 3, tokensAI: 2, damageMul: 1.40, coolMin: 0.70, coolSpan: 1.30, grantGap: 0.11 },
};

const DEATHS = ['death_a', 'death_b', 'death_launch'];
const _opts = { knockback: 0, dirX: 0, dirZ: 0, heavy: 0, fromAI: true };
const _ao = { blend: 0.06 };
/** Death clips: locked so the animator's own ST.DEAD -> clip map cannot restart
 *  them from frame 0 on the next step, and rate-jittered so twenty-five men
 *  killed by one sweep are not one animation played twenty-five times. */
const _death = { blend: 0.06, lock: 1.7, rate: 1 };

/**
 * How much of a unit's raw blow the HERO actually takes.
 *
 * Every damage number in this module was tuned against another UNIT (44-165 hp)
 * and every one of them is individually reasonable. Collectively they are not:
 * measured over a whole battle, a hero standing still in a thin ring of three
 * men lost 24.5 hp/s and the opening cinematic alone cost him 126 of his 1000.
 * A Warriors hero wades through a mob for minutes. One constant, applied at the
 * single funnel every AI blow passes through, fixes the whole economy without
 * touching the ring behaviour the AI was tuned for.
 */
const HERO_GUARD = 0.42;

export function createAI() {
  const store = new AIStore(2600);
  const squads = new Squads(160);
  const officers = [];
  const commander = createCommander();
  const q = new Int32Array(512);
  let W = null;

  const stats = {
    cpuMs: 0, cpuMsPeak: 0, cpuMsP99: 0, units: 0, aware: 0, attacking: 0, tokens: 0,
    fleeing: 0, ringing: 0, thinks: 0, squads: 0, officers: 0, moveMs: 0,
    targeted: 0, marching: 0, routing: 0, routSquads: 0, retasked: 0, engagedSquads: 0, advancing: 0,
    msCensus: 0, msSquads: 0, msCommand: 0, msUnits: 0, msOfficers: 0,
    // ---- provenance for cpuMsPeak ------------------------------------
    // `cpuMsPeak` is a LIFETIME MAX of a wall-clock sample, so across a 40 s
    // capture it reports the worst descheduling event on the machine, not the
    // worst thing this module did. Measured on the capture box at load 9.94 on
    // 4 cores: a control loop doing constant work with zero allocation has a
    // median of 0.053 ms and a max of 97.45 ms -- a 1827x ratio no code change
    // can touch. So record what the AI was actually DOING on the tick the peak
    // landed, and read it like this:
    //
    //   peakMoved / peakThinks near `moved` / `thinks` -> the box descheduled us
    //   peakMoved / peakThinks many times them         -> the AI has a spiky pass
    //
    // `moved` (bodies steered this step) is the right work proxy: separation is
    // one spatial-hash query per body and is the dominant cost in here.
    moved: 0, peakTick: 0, peakMoved: 0, peakThinks: 0, peakUnits: 0,
  };
  /** `world.ai.profile = true` breaks the step down by phase. Off by default:
   *  four performance.now() calls a step is not free at 60 Hz. */
  let prof = false;
  /** Ring buffer of per-step costs — `cpuMsPeak` is a lifetime max and one GC
   *  pause pins it for the rest of the capture, so keep an honest percentile
   *  next to it. */
  const msRing = new Float32Array(256); let msI = 0;
  const msSorted = new Float32Array(256);

  const ctx = {
    w: null, store, squads, q, officers, stats,
    tokens: 3, tokensAI: 2, damageMul: 1, coolMin: 1.0, coolSpan: 1.8, grantGap: 0.16,
    open: 0,                                      // the hero is on the floor / reeling
    morale: [0.5, 0.5, 0.5],
    allowPlayerDeath: true,                    // compatibility mirror; player owns policy
    hurt: null,                                   // filled in below
  };

  /* ------------------------------------------------------------- damage */

  /**
   * Unit-vs-unit damage.
   *
   * Deliberately NOT routed through `world.combat.damage` unless the target is
   * the player: `src/combat/impact.js` turns every `'hit'` event into global
   * hitstop and a camera shake, and six hundred men trading blows across the
   * valley would freeze the simulation solid. Blows that involve the player go
   * through combat so his systems see them; everything else resolves here and
   * announces itself on `'ai:hit'` / `'ai:kill'`.
   */
  function hurt(id, amount, srcId, dirX, dirZ, heavy = 0) {
    const w = ctx.w, e = w.entities;
    if (id < 0 || !e.alive[id] || e.state[id] === ST.DEAD) return false;

    if (id === w.playerId) {
      if (w.flags.godMode) return false;
      const amt = amount * HERO_GUARD;
      if (amt <= 0.01) return false;
      _opts.knockback = heavy >= 2 ? 4 : heavy ? 2 : 0.6;
      _opts.dirX = dirX; _opts.dirZ = dirZ; _opts.heavy = heavy;
      if (w.combat?.damage) return w.combat.damage(id, amt, srcId, _opts);
      const floor = w.player?.allowDeath === false ? 1 : 0;
      e.hp[id] = Math.max(floor, e.hp[id] - amt);
      if (e.hp[id] <= 0) w.player?.die?.(srcId);
      return amt > 0;
    }

    // i-frames are not the player's alone: an officer mid-sidestep or mid-musou
    // raises FLAG.INVULN, and combat honours it — this path did not, so a stray
    // ally could cancel his dodge and (via officerHit) strand him in HURT with
    // the flag still up.
    if (e.has(id, FLAG.INVULN)) return false;

    e.hp[id] -= amount;
    e.flash[id] = 1;

    // Officers get first refusal — a guarded blow is refunded in officerHit(),
    // so the damage has to reach him before the death test.
    let handled = false;
    if (store.role[id] === ROLE.OFFICER) handled = officerHit(ctx, id, amount, srcId, dirX, dirZ, heavy);
    if (e.hp[id] <= 0) { kill(id, srcId, dirX, dirZ, heavy); return true; }
    if (!handled) react(ctx, id, amount, srcId, dirX, dirZ, heavy);
    spark(id, dirX, dirZ, heavy);
    w.bus.emit('ai:hit', id, srcId, amount, heavy);
    return true;
  }

  ctx.hurt = hurt;

  /** A hit spark, but only where the camera can see it — the pool is small. */
  function spark(id, dirX, dirZ, heavy) {
    const w = ctx.w, e = w.entities, pid = w.playerId;
    if (pid < 0 || !w.vfx) return;
    const dx = e.x[id] - e.x[pid], dz = e.z[id] - e.z[pid];
    if (dx * dx + dz * dz > 26 * 26) return;
    w.vfx.spark(e.x[id], e.y[id] + 1.15, e.z[id], dirX, 0.7, dirZ, heavy ? 0.9 : 0.45);
  }

  function kill(id, srcId, dirX, dirZ, heavy) {
    const w = ctx.w, e = w.entities;
    e.hp[id] = 0;
    e.state[id] = ST.DEAD; e.stateT[id] = 0; e.fade[id] = 1;
    onDeath(id, srcId, dirX, dirZ, heavy);
    e.stats.allyKills++;
    w.bus.emit('ai:kill', id, srcId);
  }

  /** Shared cleanup for a body that has just died, whoever killed it. */
  function onDeath(id, srcId = -1, dirX = 0, dirZ = 0, heavy = 0) {
    const w = ctx.w, e = w.entities;
    store.ensure(e, id);
    if (store.act[id] === ACT.DEAD) return;
    dropToken(store, id);
    store.act[id] = ACT.DEAD;
    const sq0 = store.squad[id];              // read BEFORE the discharge below
    squads.leave(sq0);
    store.squad[id] = -1;
    e.setFlag(id, FLAG.AGGRO, false);
    e.vx[id] += dirX * (heavy >= 2 ? 7 : heavy ? 4 : 1.6);
    e.vz[id] += dirZ * (heavy >= 2 ? 7 : heavy ? 4 : 1.6);
    if (heavy >= 2) e.vy[id] = 4.4;
    const uid = e.uid[id];
    const key = e.spawnKey[id];
    _death.rate = 0.86 + frac01(key, 71) * 0.34;
    w.anim?.play?.(id, heavy >= 2 ? 'death_launch' : DEATHS[hash32(key) % 3], _death);

    // ---- morale shock --------------------------------------------------
    // Men who watch a comrade fall next to them lose heart, flinch, look at
    // the body and give ground. `witness()` is capped at the nearest few so a
    // musou that kills twenty-five does not cost twenty-five reactions each.
    const myTeam = e.team[id];
    const bx = e.x[id], bz = e.z[id];
    const n = e.around(bx, bz, 8.5, q, q.length);
    let reacted = 0;
    for (let i = 0; i < n; i++) {
      const o = q[i];
      if (o === id || !e.alive[o] || e.state[o] === ST.DEAD || o === w.playerId) continue;
      if (e.team[o] !== myTeam) { e.morale[o] = Math.min(1, e.morale[o] + 0.022); continue; }
      const ox = e.x[o] - bx, oz = e.z[o] - bz;
      const d2 = ox * ox + oz * oz;
      e.morale[o] = Math.max(0, e.morale[o] - (d2 < 16 ? 0.075 : 0.04));
      if (reacted < 5 && d2 < 42.25 && store.uid[o] === e.uid[o] && store.role[o] !== ROLE.OFFICER) {
        const d = Math.sqrt(d2) || 1;
        witness(ctx, o, -ox / d, -oz / d, d, d2 < 12.25);
        reacted++;
      }
    }

    // ---- the squad takes it ---------------------------------------------
    if (sq0 >= 0 && squads.alive[sq0]) {
      const wasBanner = squads.banner[sq0] === id;
      const wasLeader = squads.leader[sq0] === id;
      const bannerUp = squads.banner[sq0] >= 0 && e.alive[squads.banner[sq0]]
        && e.state[squads.banner[sq0]] !== ST.DEAD && !wasBanner;
      let hit = bannerUp ? 0.040 : 0.062;
      if (wasBanner) hit = 0.30;                     // the standard is down
      else if (wasLeader || store.role[id] === ROLE.CAPTAIN) hit = 0.19;
      squads.morale[sq0] = Math.max(0, squads.morale[sq0] - hit);
      if (wasBanner) squads.banner[sq0] = -1;
    }
    const elite = e.has(id, FLAG.ELITE);
    ctx.morale[myTeam] = clamp(ctx.morale[myTeam] - (elite ? 0.05 : 0.0016), 0.05, 0.95);
    const foe = myTeam === TEAM.ENEMY ? TEAM.PLAYER : TEAM.ENEMY;
    ctx.morale[foe] = clamp(ctx.morale[foe] + (elite ? 0.04 : 0.0011), 0.05, 0.95);

    if (store.role[id] === ROLE.OFFICER) {
      const rec = e.ext[id];
      if (rec && rec.uid === uid) {
        rec.alive = false; rec.engaged = false; rec.hp = 0;
        w.ui?.toast?.(`${rec.name} has fallen`);
        w.bus.emit('officer:down', id, srcId);
      }
      for (let i = officers.length - 1; i >= 0; i--) if (officers[i].id === id) officers.splice(i, 1);
    }
  }

  /* -------------------------------------------------------------- census */

  /**
   * One pass over the live list that rebuilds the per-target attack-token and
   * ring populations from the units that actually hold them. Rebuilding beats
   * incrementing: a token can never leak when its holder is knocked down,
   * despawned or killed by something the AI never hears about.
   */
  function census(w) {
    const e = w.entities;
    store.tokN.fill(0); store.ringN.fill(0); store.ringSlot.fill(0);
    let aware = 0, attacking = 0, tokens = 0, fleeing = 0, ringing = 0, units = 0,
      targeted = 0, marching = 0;
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (id === w.playerId || store.uid[id] !== e.uid[id]) continue;
      if (e.state[id] === ST.DEAD) continue;
      units++;
      const a = store.act[id];
      const t = store.tgt[id];
      if (t >= 0 && e.alive[t]) {
        targeted++;
        if (store.tok[id]) { store.tokN[t]++; tokens++; }
        if (a === ACT.RING || a === ACT.PRESS || a === ACT.BLOCK || a === ACT.TAUNT) { store.ringN[t]++; ringing++; }
        // Rebuild the ring-slot occupancy from the claims, exactly like the
        // token pool: a slot held by a man who has since been killed, knocked
        // down or despawned must not keep the next man out of it.
        const sc = store.sect[id];
        if (sc >= 0 && a !== ACT.FLEE) {
          const k = (t << 5) + (store.rank[id] << 4) + sc;
          if (store.ringSlot[k] < 250) store.ringSlot[k]++;
        }
      }
      if (store.alert[id]) aware++;
      if (a === ACT.WINDUP || a === ACT.SWING || a === ACT.RECOVER) attacking++;
      if (a === ACT.FLEE) fleeing++;
      if (a === ACT.MARCH || a === ACT.FORM || a === ACT.APPROACH) marching++;
    }
    stats.units = units; stats.aware = aware; stats.attacking = attacking;
    stats.tokens = tokens; stats.fleeing = fleeing; stats.ringing = ringing;
    stats.targeted = targeted; stats.marching = marching; stats.routing = fleeing;

    // Is the hero open? A man on the floor, in a guard break or still in his
    // hit reaction gets mobbed; a man holding a guard does not.
    const pid = w.playerId;
    if (pid >= 0 && e.alive[pid]) {
      const st = e.state[pid];
      ctx.open = (st === ST.DOWN || st === ST.GETUP || st === ST.STUN || st === ST.GUARD_BREAK
        || st === ST.LAUNCH || st === ST.AIRBORNE || st === ST.HIT) ? 1 : 0;
    } else ctx.open = 0;
  }

  /* ---------------------------------------------------------- squad audit */

  const _sqCount = new Int32Array(160);
  const _sqX = new Float64Array(160), _sqZ = new Float64Array(160);
  const _sqEX = new Float64Array(160), _sqEZ = new Float64Array(160);

  /**
   * Read the squads back off their men.
   *
   * `Squads` is deliberately O(squads) per think: members only ever read
   * `slotPos()` and nothing reported back, so the anchor was an open loop.
   * This is the one pass that closes it — the living centroid of each squad
   * and the mean error between a man and the slot he was given, which is what
   * `Squads.update` steers the anchor by. One loop over live units at 5 Hz,
   * accumulating four sums and one `slotPos` per man; it is deliberately NOT
   * folded into the per-unit think loop, which is where `cpuMsPeak` lives.
   */
  function trackSquadMen(w) {
    const e = w.entities;
    _sqCount.fill(0); _sqX.fill(0); _sqZ.fill(0); _sqEX.fill(0); _sqEZ.fill(0);
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (id === w.playerId || store.uid[id] !== e.uid[id]) continue;
      if (!e.alive[id] || e.state[id] === ST.DEAD) continue;
      const s = store.squad[id];
      if (s < 0 || s >= _sqCount.length || !squads.alive[s]) continue;
      const x = e.x[id], z = e.z[id];
      _sqCount[s]++; _sqX[s] += x; _sqZ[s] += z;
      const sp = squads.slotPos(s, store.slot[id]);
      _sqEX[s] += x - sp.x; _sqEZ[s] += z - sp.z;
    }
    for (let s = 0; s < squads.cap; s++) {
      if (!squads.alive[s]) continue;
      const n = s < _sqCount.length ? _sqCount[s] : 0;
      squads.tracked[s] = n;
      if (n <= 0) continue;
      squads.cx[s] = _sqX[s] / n; squads.cz[s] = _sqZ[s] / n;
      squads.sex[s] = _sqEX[s] / n; squads.sez[s] = _sqEZ[s] / n;
    }
  }

  function auditSquads(w) {
    const e = w.entities;
    _sqCount.fill(0);
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (id === w.playerId || store.uid[id] !== e.uid[id]) continue;
      if (e.state[id] === ST.DEAD) continue;
      const s = store.squad[id];
      if (s >= 0 && s < _sqCount.length) _sqCount[s]++;
    }
    for (let s = 0; s < squads.cap; s++) {
      if (!squads.alive[s]) continue;
      squads.count[s] = _sqCount[s];
      if (squads.size[s] > 0 && _sqCount[s] <= 0) squads.disband(s);
      else {
        const L = squads.leader[s];
        if (L >= 0 && (!e.alive[L] || e.state[L] === ST.DEAD)) squads.leader[s] = -1;
      }
    }
    stats.squads = squads.n;
  }

  /** Clear every battle-local AI registry without rebuilding the system. */
  function resetBattle() {
    store.uid.fill(0); store.spawnKey.fill(0); store.act.fill(0); store.prevAct.fill(0);
    store.actT.fill(0); store.dur.fill(0); store.think.fill(0);
    store.tgt.fill(0); store.tgtUid.fill(0);
    store.ringA.fill(0); store.ringR.fill(0); store.ringDir.fill(0); store.orbit.fill(0);
    store.sect.fill(0); store.rank.fill(0); store.cool.fill(0);
    store.tok.fill(0); store.tokT.fill(0);
    store.role.fill(0); store.squad.fill(0); store.slot.fill(0);
    store.spdMul.fill(0); store.agg.fill(0); store.crg.fill(0); store.phase.fill(0);
    store.alert.fill(0); store.homeX.fill(0); store.homeZ.fill(0);
    store.dvx.fill(0); store.dvz.fill(0); store.yawT.fill(0);
    store.clip.fill(0); store.flags.fill(0);
    store.tokN.fill(0); store.ringN.fill(0); store.grantT.fill(0); store.ringSlot.fill(0);

    squads.resetBattle();
    commander.resetBattle();
    resetGruntBattle();
    officers.length = 0;
    _sqCount.fill(0);
    msRing.fill(0); msSorted.fill(0); msI = 0;
    ctx.open = 0;
    ctx.morale[0] = 0.5; ctx.morale[1] = 0.5; ctx.morale[2] = 0.5;
    ctx.allowPlayerDeath = W?.player?.allowDeath ?? true;
    for (const key in stats) if (typeof stats[key] === 'number') stats[key] = 0;
    if (W?.ai) W.ai.engaged = null;
    return W?.ai || null;
  }

  /* ================================================================ system */

  return {
    name: 'ai', order: ORDER.AI,

    init(w) {
      W = w; ctx.w = w;
      Object.assign(ctx, DIFF.normal);

      const api = {
        version: 2,
        store, squads, officers, stats,
        ACT, ACT_NAME, OF, OF_NAME, ORDERS, FORM, ROLE, OBJ, OBJ_NAME, AIF,
        engaged: null,
        get morale() { return ctx.morale; },
        get tokens() { return ctx.tokens; },
        set tokens(v) { ctx.tokens = Math.max(1, v | 0); },
        get allowPlayerDeath() { return w.player?.allowDeath ?? ctx.allowPlayerDeath; },
        set allowPlayerDeath(v) {
          ctx.allowPlayerDeath = !!v;
          w.player?.setAllowDeath?.(v);
        },
        difficulty: 'normal',
        get profile() { return prof; },
        set profile(v) { prof = !!v; },

        hurt,
        resetBattle,
        setDifficulty(name) {
          const d = DIFF[name] || DIFF.normal;
          Object.assign(ctx, d);
          api.difficulty = DIFF[name] ? name : 'normal';
          return api.difficulty;
        },

        /** Bind a freshly spawned entity to the AI (called by the director). */
        enlist(id, squad = -1, role = ROLE.GRUNT) {
          const e = w.entities;
          store.bind(e, id, role);
          const u = e.spawnKey[id];
          if (squad >= 0 && squads.alive[squad]) {
            store.squad[id] = squad;
            store.slot[id] = squads.join(squad);
            e.group[id] = squad;
            squads.slotPos(squad, store.slot[id]);
          }
          if (role === ROLE.CAPTAIN) store.flags[id] |= AIF.VETERAN | AIF.SHIELD;
          else if (frac01(u, 51) < 0.22) store.flags[id] |= AIF.SHIELD;
          // Every squad carries one standard. He holds the formation, he never
          // charges, and putting him on the ground is worth a fifth of the
          // squad's morale (see onDeath) — which is what a banner is FOR.
          if (squad >= 0 && squads.alive[squad] && squads.banner[squad] < 0
              && role === ROLE.GRUNT && store.slot[id] >= 1) {
            squads.banner[squad] = id;
            store.flags[id] |= AIF.BANNER | AIF.VETERAN;
          }
          // a fifth of the rank and file are old hands
          if (role === ROLE.GRUNT && frac01(u, 57) < 0.18) store.flags[id] |= AIF.VETERAN;
          if (role === ROLE.ARCHER) store.ringR[id] = 8.0 + frac01(u, 53) * 2.5;
          return id;
        },
        discharge(id) {
          const e = w.entities;
          dropToken(store, id);
          const sq = store.squad[id];
          if (sq >= 0 && squads.alive[sq] && squads.banner[sq] === id) squads.banner[sq] = -1;
          squads.leave(sq);
          store.squad[id] = -1;
          store.uid[id] = 0;
          store.spawnKey[id] = 0;
        },

        newSquad(team, x, z, o) { return squads.create(team, x, z, o); },
        setSquadOrder(i, order, gx, gz) { squads.setOrder(i, order, gx, gz); },

        /** Order the nearest idle squads of `team` to converge on a point. */
        callToBattle(x, z, team, radius = 120, maxSquads = 2) {
          let sent = 0;
          for (let s = 0; s < squads.cap && sent < maxSquads; s++) {
            if (!squads.alive[s] || squads.team[s] !== team) continue;
            if (squads.order[s] === ORDERS.ENGAGE || squads.order[s] === ORDERS.ROUT) continue;
            const dx = squads.ax[s] - x, dz = squads.az[s] - z;
            const d = hyp(dx, dz);
            if (d > radius || d < 12) continue;
            squads.setOrder(s, ORDERS.ADVANCE, x, z);
            // a squad called to the fight jogs; at 3.1 it could never reach a
            // hero who was moving (see src/ai/grunt.js ACT.FORM)
            squads.speed[s] = 4.6;
            // Clear the squad-vs-squad objective, or the commander's stale-goal
            // check (`src/ai/command.js`) sees a goal 100 m from the squad this
            // one was last sent at and quietly re-tasks it back off the hero
            // three and a half seconds after the director called it in.
            squads.objSq[s] = -1; squads.obj[s] = OBJ.RALLY;
            sent++;
          }
          return sent;
        },

        registerOfficer(id, name) {
          const rec = makeOfficer(w, id, name ? { name } : undefined);
          store.role[id] = ROLE.OFFICER;
          officers.push(rec);
          stats.officers = officers.length;
          return rec;
        },

        /**
         * Measurement probe used by the capture scenarios: how many live foes
         * are inside `radius` of `targetId`, and how many of those are
         * actually mid-swing at this instant. The whole point of the token
         * pool is that the second number stays at 2–3 while the first climbs.
         */
        ringInfo(targetId = w.playerId, radius = 4) {
          const e = w.entities;
          if (targetId < 0) return null;
          const x = e.x[targetId], z = e.z[targetId], team = e.team[targetId];
          const n = e.around(x, z, radius + 1, q, q.length);
          let near = 0, attacking = 0, windup = 0, tokens = 0, blocking = 0,
            taunting = 0, circling = 0, approaching = 0, down = 0, fleeing = 0;
          // Order-independent signature of WHO is in the ring. `approaching`
          // cannot answer "does the ring turn over" -- it counts men already
          // inside the radius who are still in ACT.APPROACH, and a man
          // rotating in or out is in ACT.RING the whole way, so it reads 0 on
          // a churning ring and on a frozen one alike. Comparing `sig` across
          // samples is what actually separates the two.
          let sig = 0;
          const r2 = radius * radius;
          for (let i = 0; i < n; i++) {
            const id = q[i];
            if (id === targetId || !e.alive[id] || e.state[id] === ST.DEAD) continue;
            if (e.team[id] === team) continue;
            const dx = e.x[id] - x, dz = e.z[id] - z;
            if (dx * dx + dz * dz > r2) continue;
            near++;
            sig = (sig ^ Math.imul(e.uid[id] | 0, 2654435761)) >>> 0;
            const a = store.act[id];
            if (store.tok[id]) tokens++;
            if (a === ACT.WINDUP) { windup++; attacking++; }
            else if (a === ACT.SWING || a === ACT.RECOVER) attacking++;
            else if (a === ACT.BLOCK) blocking++;
            else if (a === ACT.TAUNT) taunting++;
            else if (a === ACT.RING || a === ACT.PRESS) circling++;
            else if (a === ACT.APPROACH) approaching++;
            else if (a === ACT.DOWN || a === ACT.GETUP || a === ACT.LAUNCHED) down++;
            else if (a === ACT.FLEE) fleeing++;
          }
          return { near, attacking, windup, tokens, blocking, taunting, circling, approaching, down, fleeing, sig,
            budget: targetId === w.playerId ? ctx.tokens : ctx.tokensAI };
        },

        /**
         * Hold a unit in a stun/flinch for `seconds`. Called by the combat
         * module when a moveset's stun property is longer than the AI's own
         * flinch (see src/combat/combat.js applyImpulse).
         */
        stagger(id, seconds = 0.5, heavy = 1) {
          const e = w.entities;
          if (id < 0 || !e.alive[id] || e.state[id] === ST.DEAD || id === w.playerId) return false;
          store.ensure(e, id);
          if (store.role[id] === ROLE.OFFICER) {
            const rec = e.ext[id];
            if (rec && rec.uid === e.uid[id]) {
              rec.of = OF.HURT; rec.ofT = 0; rec.dur = Math.max(rec.dur, seconds); rec.hit = 0;
              e.setFlag(id, FLAG.SUPERARMOR, false);
            }
          } else {
            dropToken(store, id);
            store.act[id] = ACT.FLINCH; store.actT[id] = 0;
            store.dur[id] = Math.max(store.dur[id], seconds);
            store.think[id] = seconds + 0.05;
          }
          e.state[id] = ST.STUN; e.stateT[id] = 0;
          e.stagger[id] = Math.max(e.stagger[id], heavy ? 1 : 0.5);
          w.anim?.play?.(id, 'hit_heavy', _ao);
          return true;
        },

        /** Debug: what is one unit doing? */
        describe(id) {
          const e = w.entities;
          return { id, uid: e.uid[id], spawnKey: e.spawnKey[id], act: ACT_NAME[store.act[id]], state: e.state[id],
            tgt: store.tgt[id], tok: store.tok[id], squad: store.squad[id], slot: store.slot[id],
            alert: store.alert[id], morale: +e.morale[id].toFixed(2), cool: +store.cool[id].toFixed(2) };
        },
      };
      w.ai = api;

      /* ---- reactions to the outside world ---------------------------- */

      // The player's combat system announces its hits here.
      w.bus.on('hit', (id, srcId, amount, opts) => {
        const e = w.entities;
        if (id < 0 || id === w.playerId || !e.alive[id]) return;
        if (e.kind[id] === KIND.PLAYER) return;
        if (opts && opts.fromAI) return;                      // our own blow, already handled
        store.ensure(e, id);
        if (e.state[id] === ST.DEAD) { onDeath(id, srcId, opts?.dirX || 0, opts?.dirZ || 0, 2); return; }
        let dx = opts?.dirX ?? 0, dz = opts?.dirZ ?? 0;
        if (dx === 0 && dz === 0 && srcId >= 0 && e.alive[srcId]) {
          dx = e.x[id] - e.x[srcId]; dz = e.z[id] - e.z[srcId];
          const d = hyp(dx, dz) || 1; dx /= d; dz /= d;
        }
        const heavy = opts?.launch ? 2 : (opts?.guardBreak || (opts?.knockback || 0) > 3 || amount > 55) ? 1 : 0;
        if (store.role[id] === ROLE.OFFICER) { if (officerHit(ctx, id, amount, srcId, dx, dz, heavy)) return; }
        react(ctx, id, amount, srcId, dx, dz, heavy);
      });

      w.bus.on('kill', (id, srcId) => {
        const e = w.entities;
        if (id < 0 || id === w.playerId || !e.alive[id]) return;
        onDeath(id, srcId, 0, 0, 1);
      });

      w.bus.on('base:captured', (b) => {
        if (!b) return;
        const t = b.team === TEAM.ENEMY ? TEAM.ENEMY : TEAM.PLAYER;
        const foe = t === TEAM.ENEMY ? TEAM.PLAYER : TEAM.ENEMY;
        ctx.morale[t] = clamp(ctx.morale[t] + 0.12, 0.05, 0.95);
        ctx.morale[foe] = clamp(ctx.morale[foe] - 0.10, 0.05, 0.95);
      });
    },

    fixedUpdate(w, dt) {
      const t0 = performance.now();
      const e = w.entities;
      ctx.w = w;
      const pid = w.playerId;

      let tp = prof ? performance.now() : 0;
      census(w);
      if (prof) { const n = performance.now(); stats.msCensus = n - tp; tp = n; }
      squads.update(w, dt);
      if (prof) { const n = performance.now(); stats.msSquads = n - tp; tp = n; }
      // One slice of re-tasking per step — never every squad on one tick, or
      // the frame that re-planned the map would blow the whole CPU budget.
      commander.tick(ctx, dt);
      if (prof) { const n = performance.now(); stats.msCommand = n - tp; tp = n; }

      const tick = w.time.tick;
      let thinks = 0, moved = 0;

      for (let i = 0; i < e.liveN; i++) {
        const id = e.live[i];
        if (id === pid || e.kind[id] === KIND.PLAYER) continue;
        store.ensure(e, id);

        // ---- the dead ---------------------------------------------------
        if (e.state[id] === ST.DEAD) {
          if (store.act[id] !== ACT.DEAD) onDeath(id);
          e.stateT[id] += dt;
          deathSlide(w, id, dt);
          if (e.stateT[id] > 8) e.release(id);       // safety net, combat normally fades them
          continue;
        }
        e.stateT[id] += dt;

        // ---- think ------------------------------------------------------
        if (store.role[id] === ROLE.OFFICER) {
          officerAct(ctx, id, dt);
        } else {
          store.think[id] -= dt;
          if (store.think[id] <= 0) { think(ctx, id); thinks++; }
          if (act(ctx, id, dt)) continue;            // handled its own physics
        }

        // ---- move -------------------------------------------------------
        separate(w, id, dt);
        integrate(w, store, id, dt, 0);
        moved++;
      }
      stats.thinks = thinks; stats.moved = moved;
      if (prof) { const n = performance.now(); stats.msUnits = n - tp; tp = n; }

      // ---- officer roster ------------------------------------------------
      let best = null, bd = 1e9;
      for (let i = 0; i < officers.length; i++) {
        const r = officers[i];
        if (!e.alive[r.id] || e.uid[r.id] !== r.uid) { r.alive = false; officers.splice(i--, 1); continue; }
        r.hp = e.hp[r.id]; r.alive = e.state[r.id] !== ST.DEAD;
        if (r.engaged && r.alive && r.dist < bd) { bd = r.dist; best = r; }
      }
      w.ai.engaged = best;
      stats.officers = officers.length;
      if (prof) stats.msOfficers = performance.now() - tp;

      // ---- housekeeping --------------------------------------------------
      if ((tick % 12) === 0) trackSquadMen(w);
      if ((tick % 61) === 0) {
        auditSquads(w);
        let eng = 0, adv = 0, rout = 0;
        for (let sIdx = 0; sIdx < squads.cap; sIdx++) {
          if (!squads.alive[sIdx]) continue;
          const o = squads.order[sIdx];
          if (o === ORDERS.ENGAGE) eng++;
          else if (o === ORDERS.ADVANCE || o === ORDERS.ESCORT) adv++;
          else if (o === ORDERS.ROUT) rout++;
        }
        stats.engagedSquads = eng; stats.advancing = adv; stats.routSquads = rout;
      }
      if ((tick % 30) === 0) {
        // team morale drifts back to parity so one bad minute is not permanent
        ctx.morale[0] += (0.5 - ctx.morale[0]) * 0.004;
        ctx.morale[1] += (0.5 - ctx.morale[1]) * 0.004;
        if (w.mission && w.mission.morale) { w.mission.morale[0] = ctx.morale[0]; w.mission.morale[1] = ctx.morale[1]; }
      }

      const ms = performance.now() - t0;
      stats.cpuMs = stats.cpuMs * 0.9 + ms * 0.1;
      msRing[msI = (msI + 1) & 255] = ms;
      if ((tick & 63) === 0 && tick > 300) {
        // The worst 99th percentile of any 256-step window in the run. It sits
        // next to `cpuMsPeak` on purpose: the lifetime max is pinned for the
        // rest of a capture by a single GC or tier-up pause landing inside this
        // window, and that number says nothing about what the AI costs.
        msSorted.set(msRing); msSorted.sort();
        if (msSorted[252] > stats.cpuMsP99) stats.cpuMsP99 = msSorted[252];
      }
      // The director seeds ~500 units on the first step (inside the boot
      // warm-up frame, behind the curtain); that one spike is not the cost of
      // running the AI, so the peak only starts counting once the field is up.
      if (tick > 90 && ms > stats.cpuMsPeak) {
        stats.cpuMsPeak = ms;
        stats.peakTick = tick; stats.peakMoved = moved;
        stats.peakThinks = thinks; stats.peakUnits = stats.units;
      }
    },
  };
}
